import React, { useState } from 'react';
import { 
  Plus, 
  Check, 
  Trash2, 
  Edit, 
  Calendar,
  Flag,
  Search,
  Filter,
  CheckCircle2,
  Circle,
  Clock
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  category: string;
  createdAt: Date;
}

const SimpleTodo: React.FC = () => {
  const { colors } = useTheme();
  const [todos, setTodos] = useState<TodoItem[]>([
    {
      id: '1',
      text: 'Revisar apuntes de matemáticas',
      completed: false,
      priority: 'high',
      dueDate: '2025-01-25',
      category: 'Estudio',
      createdAt: new Date('2025-01-20')
    },
    {
      id: '2',
      text: 'Comprar materiales para proyecto',
      completed: true,
      priority: 'medium',
      category: 'Personal',
      createdAt: new Date('2025-01-19')
    },
    {
      id: '3',
      text: 'Preparar presentación final',
      completed: false,
      priority: 'high',
      dueDate: '2025-01-30',
      category: 'Trabajo',
      createdAt: new Date('2025-01-18')
    },
    {
      id: '4',
      text: 'Hacer ejercicio',
      completed: false,
      priority: 'low',
      category: 'Personal',
      createdAt: new Date('2025-01-21')
    }
  ]);

  const [newTodo, setNewTodo] = useState('');
  const [newPriority, setNewPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [newCategory, setNewCategory] = useState('Personal');
  const [newDueDate, setNewDueDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const categories = ['Personal', 'Estudio', 'Trabajo', 'Salud', 'Hogar'];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return colors.red;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <Flag className="w-3 h-3" />;
      case 'medium': return <Clock className="w-3 h-3" />;
      case 'low': return <CheckCircle2 className="w-3 h-3" />;
      default: return <Flag className="w-3 h-3" />;
    }
  };

  const addTodo = () => {
    if (newTodo.trim()) {
      const todo: TodoItem = {
        id: Date.now().toString(),
        text: newTodo.trim(),
        completed: false,
        priority: newPriority,
        dueDate: newDueDate || undefined,
        category: newCategory,
        createdAt: new Date()
      };
      
      setTodos(prev => [todo, ...prev]);
      setNewTodo('');
      setNewDueDate('');
      setNewPriority('medium');
    }
  };

  const toggleTodo = (id: string) => {
    setTodos(prev => prev.map(todo => 
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const deleteTodo = (id: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  };

  const startEdit = (todo: TodoItem) => {
    setEditingId(todo.id);
    setEditText(todo.text);
  };

  const saveEdit = () => {
    if (editText.trim() && editingId) {
      setTodos(prev => prev.map(todo => 
        todo.id === editingId ? { ...todo, text: editText.trim() } : todo
      ));
      setEditingId(null);
      setEditText('');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const filteredTodos = todos.filter(todo => {
    const matchesSearch = todo.text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || todo.priority === filterPriority;
    const matchesCategory = filterCategory === 'all' || todo.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'completed' && todo.completed) ||
      (filterStatus === 'pending' && !todo.completed);
    
    return matchesSearch && matchesPriority && matchesCategory && matchesStatus;
  });

  const stats = {
    total: todos.length,
    completed: todos.filter(t => t.completed).length,
    pending: todos.filter(t => !t.completed).length,
    overdue: todos.filter(t => 
      t.dueDate && new Date(t.dueDate) < new Date() && !t.completed
    ).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Lista de Tareas
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          className="p-4 rounded-lg text-center"
          style={{ backgroundColor: colors.mantle }}
        >
          <div className="text-2xl font-bold" style={{ color: colors.text }}>
            {stats.total}
          </div>
          <div className="text-sm" style={{ color: colors.subtext0 }}>
            Total
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{ backgroundColor: colors.mantle }}
        >
          <div className="text-2xl font-bold" style={{ color: colors.yellow }}>
            {stats.pending}
          </div>
          <div className="text-sm" style={{ color: colors.subtext0 }}>
            Pendientes
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{ backgroundColor: colors.mantle }}
        >
          <div className="text-2xl font-bold" style={{ color: colors.green }}>
            {stats.completed}
          </div>
          <div className="text-sm" style={{ color: colors.subtext0 }}>
            Completadas
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{ backgroundColor: colors.mantle }}
        >
          <div className="text-2xl font-bold" style={{ color: colors.red }}>
            {stats.overdue}
          </div>
          <div className="text-sm" style={{ color: colors.subtext0 }}>
            Vencidas
          </div>
        </div>
      </div>

      {/* Add Todo Form */}
      <div 
        className="p-6 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="space-y-4">
          <div className="flex gap-4">
            <input
              type="text"
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTodo()}
              placeholder="Agregar nueva tarea..."
              className="flex-1 px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            />
            
            <button
              onClick={addTodo}
              className="px-6 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: colors.blue }}
            >
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value as any)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              <option value="low">Baja prioridad</option>
              <option value="medium">Media prioridad</option>
              <option value="high">Alta prioridad</option>
            </select>
            
            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div 
        className="p-4 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: colors.subtext0 }}
              />
              <input
                type="text"
                placeholder="Buscar tareas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              <option value="all">Todas</option>
              <option value="pending">Pendientes</option>
              <option value="completed">Completadas</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              <option value="all">Todas las categorías</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              <option value="all">Todas las prioridades</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
          </div>
        </div>
      </div>

      {/* Todo List */}
      <div 
        className="rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="p-4 border-b" style={{ borderColor: colors.surface0 }}>
          <h3 className="font-semibold" style={{ color: colors.text }}>
            Tareas ({filteredTodos.length})
          </h3>
        </div>
        
        <div className="divide-y" style={{ borderColor: colors.surface1 }}>
          {filteredTodos.map((todo) => {
            const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && !todo.completed;
            const isEditing = editingId === todo.id;
            
            return (
              <div
                key={todo.id}
                className={`p-4 transition-all ${todo.completed ? 'opacity-60' : ''}`}
                style={{ backgroundColor: colors.base }}
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleTodo(todo.id)}
                    className="flex-shrink-0"
                  >
                    {todo.completed ? (
                      <CheckCircle2 className="w-5 h-5" style={{ color: colors.green }} />
                    ) : (
                      <Circle className="w-5 h-5" style={{ color: colors.subtext0 }} />
                    )}
                  </button>
                  
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="flex-1 px-2 py-1 rounded border"
                          style={{
                            backgroundColor: colors.mantle,
                            borderColor: colors.surface1,
                            color: colors.text
                          }}
                          autoFocus
                        />
                        <button
                          onClick={saveEdit}
                          className="p-1 rounded text-white"
                          style={{ backgroundColor: colors.green }}
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 rounded text-white"
                          style={{ backgroundColor: colors.red }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <span 
                            className={`${todo.completed ? 'line-through' : ''}`}
                            style={{ color: colors.text }}
                          >
                            {todo.text}
                          </span>
                          
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{ 
                                backgroundColor: colors.surface0,
                                color: colors.text
                              }}
                            >
                              {todo.category}
                            </span>
                            
                            <div 
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                              style={{ 
                                backgroundColor: getPriorityColor(todo.priority) + '20',
                                color: getPriorityColor(todo.priority)
                              }}
                            >
                              {getPriorityIcon(todo.priority)}
                              {todo.priority}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs" style={{ color: colors.subtext0 }}>
                          <span>
                            Creada: {todo.createdAt.toLocaleDateString()}
                          </span>
                          
                          {todo.dueDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
                                Vence: {new Date(todo.dueDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {!isEditing && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(todo)}
                        className="p-1 rounded hover:opacity-80"
                        style={{ color: colors.subtext0 }}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => deleteTodo(todo.id)}
                        className="p-1 rounded hover:opacity-80"
                        style={{ color: colors.red }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {filteredTodos.length === 0 && (
            <div className="p-8 text-center">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: colors.surface0 }}
              >
                <CheckCircle2 className="w-8 h-8" style={{ color: colors.subtext0 }} />
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: colors.text }}>
                No hay tareas
              </h3>
              <p style={{ color: colors.subtext0 }}>
                {searchTerm || filterPriority !== 'all' || filterCategory !== 'all' || filterStatus !== 'all'
                  ? 'No se encontraron tareas con los filtros aplicados'
                  : 'Agrega tu primera tarea para comenzar'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleTodo;